import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../../auth/AuthContext';
import './Register.css';

const Register = () => {
    const [isBuyer, setIsBuyer] = useState(true);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        interests: '',
        businessName: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [registrationComplete, setRegistrationComplete] = useState(false);
    
    const { register } = useContext(AuthContext);
    const navigate = useNavigate();
    
    const toggleRole = () => {
        setIsBuyer(!isBuyer);
    };
    
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };
    
    const validateForm = () => {
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return false;
        }
        
        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters long');
            return false;
        }
        
        return true;
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        // Validate form
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);
        
        try {
            // Prepare user data for registration
            const userData = {
                name: formData.name,
                email: formData.email,
            };
            
            if (isBuyer) {
                userData.interests = formData.interests;
            } else {
                userData.business_name = formData.businessName;
            }
            
            // Register the user with enhanced AuthContext
            const { success, user, error, emailConfirmationRequired, message } = await register(
                formData.email, 
                formData.password, 
                userData, 
                isBuyer ? 'buyer' : 'seller'
            );
            
            if (!success) {
                setError(error.message || 'Registration failed. Please try again.');
                return;
            }
            
            // Registration successful
            setRegistrationComplete(true);
            
            // If there's a specific message from the registration process, use it
            if (message) {
                setFormData({
                    ...formData,
                    registrationMessage: message
                });
            }
            
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            console.error('Registration error:', err);
        } finally {
            setLoading(false);
        }
    };

    if (registrationComplete) {
        return (
            <main className="register-page">
                <div className="container">
                    <div className="success-message registration-success">
                        <h2>Registration Successful!</h2>
                        <p>{formData.registrationMessage || `A confirmation email has been sent to ${formData.email}.`}</p>
                        <p>Please check your inbox and click the confirmation link to verify your account.</p>
                        <div className="action-buttons">
                            <Link to="/login" className="primary-button">Go to Login</Link>
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="register-page">
            <div className="container">
                <header className="register-header">
                    <h1>Register as a {isBuyer ? 'Buyer' : 'Seller'}</h1>
                    <button className="role-switcher" onClick={toggleRole}>
                        Switch to {isBuyer ? 'Seller' : 'Buyer'}
                    </button>
                </header>
                
                {error && <div className="error-message">{error}</div>}

                <form className="register-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="name">Name</label>
                        <input 
                            type="text" 
                            id="name" 
                            name="name" 
                            value={formData.name}
                            onChange={handleChange}
                            required 
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input 
                            type="email" 
                            id="email" 
                            name="email" 
                            value={formData.email}
                            onChange={handleChange}
                            required 
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <input 
                            type="password" 
                            id="password" 
                            name="password" 
                            value={formData.password}
                            onChange={handleChange}
                            required 
                            disabled={loading}
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <input 
                            type="password" 
                            id="confirmPassword" 
                            name="confirmPassword" 
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            required 
                            disabled={loading}
                        />
                    </div>

                    {isBuyer ? (
                        <div className="form-group">
                            <label htmlFor="interests">Interests</label>
                            <input 
                                type="text" 
                                id="interests" 
                                name="interests" 
                                value={formData.interests}
                                onChange={handleChange}
                                placeholder="e.g., cars, boats" 
                                disabled={loading}
                            />
                        </div>
                    ) : (
                        <div className="form-group">
                            <label htmlFor="businessName">Business Name</label>
                            <input 
                                type="text" 
                                id="businessName" 
                                name="businessName" 
                                value={formData.businessName}
                                onChange={handleChange}
                                required 
                                disabled={loading}
                            />
                        </div>
                    )}

                    <button 
                        type="submit" 
                        className="submit-button"
                        disabled={loading}
                    >
                        {loading ? 'Registering...' : 'Register'}
                    </button>
                </form>
                
                <div className="form-footer">
                    <p>Already have an account? <Link to="/login">Login</Link></p>
                </div>
            </div>
        </main>
    );
};

export default Register; 